// import { Resizer } from './Resizer';
import '@rob-cc/phaser/plugins/spine/dist/SpinePlugin';
// if (__IS_PRODUCTION__) import(/* webpackMode: "eager" */ 'phaser/plugins/spine/dist/SpinePlugin');
// else import(/* webpackMode: "eager" */ 'phaser/plugins/spine/dist/SpinePluginDebug');
import BaseScene from './scenes/BaseScene';
import { URLOptionsParser } from './utils/URLOptionsParser';
import { DataHandler } from './modules/DataHandler';
import { GameDataHandler } from '../GameDataHandler';
import { AudioHandler } from './modules/AudioHandler';
import { LocaleHandler } from './modules/LocaleHandler';
import { AssetManager, SceneAssets } from './modules/AssetManager';
import TransitionScene from './scenes/TransitionScene';

export interface BootConfig {
  env: 'dev' | 'prod';
  lang: string;
  bootScene: string;
  targetScene?: string;
  defaultScene: string;
  localisationsKey: string;
}

export interface TransitionConfig {
  nextScene: string;
  shouldUnloadUnusedAssets?: boolean;
  forceUnloadAssetPacks?: string[];
  transitionTime?: number;
  transitionScene?: string;
  waitForPreload: boolean;
}

export const instanceOfTransitionConfig = (object: any): object is TransitionConfig => {
  return typeof object === 'object' && 'nextScene' in object;
};

const defaultBootConfig: BootConfig = {
  env: __IS_PRODUCTION__ ? 'prod' : 'dev',
  lang: __LANG__,
  bootScene: 'Boot',
  defaultScene: 'Error',
  localisationsKey: 'localisations'
};

const defaultGameConfig: Phaser.Types.Core.GameConfig = {
  width: 1920,
  height: 768,
  backgroundColor: '#2f2f2f',
  scale: {
    mode: Phaser.Scale.ScaleModes.HEIGHT_CONTROLS_WIDTH,
    // mode: Phaser.Scale.ScaleModes.FIT,
    // mode: Phaser.Scale.NONE,
    autoCenter: Phaser.Scale.Center.CENTER_BOTH,
    // min: { width: 1 },
    parent: 'game',
    fullscreenTarget: 'game'
  },
  plugins: {
    scene: [
      {
        key: 'SpinePlugin',
        plugin: window.SpinePlugin,
        mapping: 'spine'
      }
    ]
  }
};

console.log(`%cIs Production: ${__IS_PRODUCTION__}`, 'background: #000; color: #fff');
console.log(`%cGame version: ${__VERSION__}`, 'background: #000; color: #fff');

export class App extends Phaser.Events.EventEmitter {
  public static events = {
    FontsLoaded: 'fontsLoaded'
  } as const;

  protected config: BootConfig;
  protected gameConfig: Phaser.Types.Core.GameConfig;
  protected width: number;
  protected height: number;
  protected safeWidth: number;
  protected safeHeight: number;
  protected sceneMap: Map<string, string | TransitionConfig>;
  protected queryOverrideTransition: string;
  protected game: Phaser.Game | undefined;

  // Modules
  dataHandler: DataHandler;
  protected gameDataHandler: GameDataHandler;
  protected assetManager: AssetManager;
  localeHandler: LocaleHandler;
  audioHandler: AudioHandler;

  constructor() {
    super();

    this.config = URLOptionsParser.parse(defaultBootConfig);
    this.gameConfig = defaultGameConfig;
    console.log(this.config);

    this.width = 1920;
    this.height = 768;
    this.safeWidth = 1280;
    this.safeHeight = 768;

    this.sceneMap = new Map();
    this.queryOverrideTransition = 'PreloadEnd';

    let defaultData = {};
    this.dataHandler = new DataHandler(__DATA_ID__, defaultData);
    this.gameDataHandler = new GameDataHandler(this.dataHandler.load());

    this.assetManager = new AssetManager();
    this.localeHandler = new LocaleHandler();

    this.audioHandler = new AudioHandler();
    this.audioHandler.makeChannel('sfx', false);
    this.audioHandler.makeChannel('music', true);
  }

  get Config() {
    return this.config;
  }

  get GameConfig() {
    return this.gameConfig;
  }

  get Game() {
    return this.game;
  }

  get AssetManager(): AssetManager {
    return this.assetManager;
  }

  get AudioHandler(): AudioHandler {
    return this.audioHandler;
  }

  public updateSceneAssetMaps = (assetLookup: Map<string, any>, baseAssets: Set<string>, sceneIdToAssets: Map<string, SceneAssets>) => {
    this.assetManager.setAssetMaps(assetLookup, baseAssets, sceneIdToAssets);
  };

  public updateSceneMap = (sceneMap: Map<string, string | TransitionConfig>, queryOverrideTransition: string) => {
    this.sceneMap = sceneMap;
    this.queryOverrideTransition = queryOverrideTransition;
  };

  public gotoNextScene = (scene: BaseScene, transitionPoint: string, data: { sourceTransition?: string; [key: string]: any } = {}) => {
    let shouldWaitForPreload: boolean = false;

    data.sourceTransition = transitionPoint;

    let config: Phaser.Types.Scenes.SceneTransitionConfig = {
      target: this.config.defaultScene,
      data: data
    };

    if (transitionPoint == this.queryOverrideTransition && this.config.targetScene) {
      config.target = this.config.targetScene;
    } else if (this.sceneMap.has(transitionPoint)) {
      let transitionConfig = this.sceneMap.get(transitionPoint);
      if (transitionConfig && typeof transitionConfig === 'string') {
        config.target = transitionConfig;
      } else if (instanceOfTransitionConfig(transitionConfig)) {
        shouldWaitForPreload = transitionConfig.waitForPreload;

        if (transitionConfig.transitionScene) {
          config.target = transitionConfig.transitionScene;
          // config.moveAbove = true;
          if (!config.data.transitionData) config.data.transitionData = {};
          Object.assign(config.data.transitionData, {
            sourceScene: scene.scene.key,
            nextScene: transitionConfig.nextScene
          });
        }
        if (transitionConfig.transitionTime) config.duration = transitionConfig.transitionTime;
      }
    }

    if (config?.data?.transitionData?.nextScene) {
      console.log(
        '%c%s%c%s',
        'background: #8D72E1; color: #fff; padding: 2px 5px; border-radius: 5px; margin-right: 8px;',
        'Transition',
        'color: #6C4AB6; padding: 2px;',
        `Transitioning from ${transitionPoint} to ${config.data.transitionData.nextScene} with transition scene ${config.target}`
      );
    } else {
      console.log(
        '%c%s%c%s',
        'background: #8D72E1; color: #fff; padding: 2px 5px; border-radius: 5px; margin-right: 8px;',
        'Transition',
        'color: #6C4AB6; padding: 2px;',
        `Transitioning from ${transitionPoint} to ${config.target}`
      );
    }

    if (shouldWaitForPreload) {
      let targetScene = scene.scene.get(config.target);
      if (targetScene instanceof TransitionScene) scene.startTransition(targetScene, config);
    } else {
      scene.scene.transition(config);
    }
  };

  // public nextScene = (scene: Phaser.Scene, key: string): string => {
  //   let targetScene = this.config.defaultScene;

  //   if (this.sceneMap.has(key)) targetScene = this.sceneMap.get(key)!;

  //   if (key == this.queryOverrideTransition && this.config.targetScene) targetScene = this.config.targetScene;

  //   console.log(`Rooting ${key} to ${targetScene}`);
  //   return targetScene;
  // };

  public afterGame(game: Phaser.Game, fn: () => void) {
    this.game = game;
    this.gameDataHandler.applySavedDataToPhaserRegistry(this.game);
    this.gameDataHandler.applyDefaultsToPhaserRegistry(this.game);
    this.gameDataHandler.bindToPhaserRegistry(this.game);

    // let resizer = new Resizer(this.game, 1920, 768, 1280, 768);
    // window.addEventListener('resize', () => {
    //   resizer.calcResize();
    //   resizer.applyResize();
    // });

    console.log(this.game.config);
    console.log(this.game?.textures);

    fn();
  }

  public beforePreload = () => {
    console.log(this.game?.config);
    console.log(this.game?.textures);
  };

  public afterPreload = (fn: () => void) => {
    console.log(this.game?.textures);

    if (this.game?.cache.json.has(this.config.localisationsKey)) {
      this.localeHandler.init(this.game.cache.json.get(this.config.localisationsKey), this.config.lang);
    }

    fn();
  };

  // public unload = (scene: Phaser.Scene, assetPackUrl: any) => {
  //   let animData = scene.anims.toJSON().anims;

  //   const dataJSON = {
  //     section1: {
  //       files: [
  //         {
  //           type: 'multiatlas',
  //           url: 'assets/atlasses/test.json',
  //           path: 'assets/atlasses',
  //           key: 'test'
  //         }
  //       ]
  //     },
  //     meta: {
  //       app: 'Phaser Editor 2D - Asset Pack Editor',
  //       contentType: 'phasereditor2d.pack.core.AssetContentType',
  //       url: 'https://phasereditor2d.com',
  //       version: 2,
  //       showAllFilesInBlocks: false
  //     }
  //   };

  //   dataJSON.section1.files.forEach((file) => {
  //     if (file.type == 'multiatlas') {
  //       scene.textures.remove(file.key);

  //       let animations = animData.filter((val) => {
  //         console.log(val);
  //         return val.key.indexOf(file.key + '-') == 0;
  //       });

  //       for (let i = 0; i < animations.length; i++) {
  //         scene.anims.remove(animations[i].key);
  //       }
  //     }
  //   });
  // };
}

export const app = new App();
